import type { ResumeSessionResponse } from "@/D2C/features/auth/utils/resumeJourney";
import { LOB_IDS } from "@/shared/common/static";
import type { PersistedUploadedDocument } from "@/shared/types/documentuploadtype";

type SessionAttachment = {
  docName?: string;
  docType?: string;
  doc_url?: string;
  size?: number;
  uploadedAt?: string;
  lastModified?: number;
};

type UploadedDocumentMap = Partial<Record<string, PersistedUploadedDocument>>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/** Maps API attachment keys to D2C upload field keys (travel). */
export function normalizeTravelAttachmentFieldKey(attachmentKey: string): string {
  if (attachmentKey === "visa") return "visaCopy";

  const legacyTravellerMatch = attachmentKey.match(/^t(\d+)_(.+)$/);
  if (legacyTravellerMatch) {
    const travellerIndex = Number(legacyTravellerMatch[1]) - 1;
    const rawField = legacyTravellerMatch[2] === "visa" ? "visaCopy" : legacyTravellerMatch[2];
    return travellerIndex <= 0 ? rawField : `traveller_${travellerIndex}_${rawField}`;
  }

  return attachmentKey;
}

function readAttachments(stepData: unknown): Record<string, SessionAttachment> {
  if (!isRecord(stepData)) return {};

  const nested = stepData.attachments || stepData.documents;
  if (Array.isArray(nested)) {
    return nested.reduce<Record<string, SessionAttachment>>((acc, item, index) => {
      if (!isRecord(item)) return acc;
      const key = String(item.fieldKey || item.field_key || item.key || item.attachType || index);
      acc[key] = item as SessionAttachment;
      return acc;
    }, {});
  }

  if (isRecord(nested)) {
    return nested as Record<string, SessionAttachment>;
  }

  return {};
}

function resolveDocumentFileUrl(docUrl?: string) {
  if (!docUrl) return "";
  if (docUrl.startsWith("http://") || docUrl.startsWith("https://")) return docUrl;

  const apiBase =
    import.meta.env.VITE_APP_ENDPOINT_URL ||
    "https://insurance-backend-fastapi-dev-c8bsctfrdqawa5fs.centralindia-01.azurewebsites.net/api/v1";
  const assetBase = apiBase.replace(/\/api\/v1\/?$/, "");

  return docUrl.startsWith("/") ? `${assetBase}${docUrl}` : `${assetBase}/${docUrl}`;
}

function toPersistedDocument(
  fieldKey: string,
  attachment: SessionAttachment
): PersistedUploadedDocument | null {
  const fileName = attachment.docName || attachment.doc_url?.split("/").pop() || "";
  if (!fileName && !attachment.doc_url) return null;

  return {
    fieldKey,
    filename: fileName,
    fileName,
    mimeType: attachment.docType || "",
    size: attachment.size,
    uploadedAt: attachment.uploadedAt || new Date().toISOString(),
    fileUrl: resolveDocumentFileUrl(attachment.doc_url),
    ocrEnabled: false,
    ocrRequested: false,
    ocrStatus: "not_requested",
    uploadResponse: attachment.doc_url
      ? { payload: { final_filename: attachment.doc_url } }
      : undefined,
  };
}

function buildTravelPersistedDocuments(sessionData: ResumeSessionResponse): UploadedDocumentMap {
  const attachments = readAttachments(sessionData.step_5_post_request);

  return Object.entries(attachments).reduce<UploadedDocumentMap>((documents, [key, attachment]) => {
    const fieldKey = normalizeTravelAttachmentFieldKey(key);
    const persisted = toPersistedDocument(fieldKey, attachment);
    if (persisted) {
      documents[fieldKey] = persisted;
    }
    return documents;
  }, {});
}

function buildLifePersistedDocuments(sessionData: ResumeSessionResponse): UploadedDocumentMap {
  // Life's doc-upload save advances the session to step_6, so read from step_6 first,
  // falling back to step_5 for older sessions saved before that change.
  const attachments = readAttachments(
    sessionData.step_5_post_request || sessionData.step_6_post_request
  );

  return Object.entries(attachments).reduce<UploadedDocumentMap>((documents, [key, attachment]) => {
    const fieldKey = normalizeTravelAttachmentFieldKey(key);
    const persisted = toPersistedDocument(fieldKey, attachment);
    if (persisted) {
      documents[fieldKey] = persisted;
    }
    return documents;
  }, {});
}

function buildGenericPersistedDocuments(sessionData: ResumeSessionResponse): UploadedDocumentMap {
  const attachments = readAttachments(sessionData.step_5_post_request);

  return Object.entries(attachments).reduce<UploadedDocumentMap>((documents, [key, attachment]) => {
    const persisted = toPersistedDocument(key, attachment);
    if (persisted) {
      documents[key] = persisted;
    }
    return documents;
  }, {});
}

export type SessionPersistedDocuments = {
  travel: UploadedDocumentMap;
  car: UploadedDocumentMap;
  health: UploadedDocumentMap;
};

export function hydrateUploadedDocumentsFromSession(
  sessionData: ResumeSessionResponse,
  lobId: number
): SessionPersistedDocuments {
  if (lobId === LOB_IDS.TRAVEL) {
    return {
      travel: buildTravelPersistedDocuments(sessionData),
      car: {},
      health: {},
    };
  }
  if (lobId === LOB_IDS.LIFE) {
    return {
      travel: buildLifePersistedDocuments(sessionData),
      car: {},
      health: {},
    };
  }
  if (lobId === LOB_IDS.MOTOR) {
    return {
      travel: {},
      car: buildGenericPersistedDocuments(sessionData),
      health: {},
    };
  }
  if (lobId === LOB_IDS.HEALTH) {
    return {
      travel: {},
      car: {},
      health: buildGenericPersistedDocuments(sessionData),
    };
  }
  return { travel: {}, car: {}, health: {} };
}
